// src/components/Notification.jsx - Versione ottimizzata con animazioni fluide
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './notification-optimized.css';

/**
 * Componente Notification ottimizzato con animazioni fluide
 */
const Notification = ({ 
  message, 
  isVisible, 
  onClose, 
  type = 'info',
  duration = 6000,
  showProgressBar = true,
  autoClose = true,
  size = 'normal',
  playSound = true,
  enableVibration = true
}) => {
  const [progress, setProgress] = useState(100);
  const [isEntering, setIsEntering] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const progressIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const soundPlayedRef = useRef(false);

  // Suoni ottimizzati (più semplici e veloci)
  const playNotificationSound = useCallback((soundType) => {
    if (!playSound || soundPlayedRef.current) return;
    
    soundPlayedRef.current = true;
    
    try {
      // Usa Web Audio API per suoni più rapidi
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configurazioni suoni semplificate
      const soundConfig = {
        success: { frequency: 800, duration: 0.15 },
        error: { frequency: 300, duration: 0.2 },
        warning: { frequency: 600, duration: 0.15 },
        info: { frequency: 500, duration: 0.1 }
      };
      
      const config = soundConfig[soundType] || soundConfig.info;
      
      oscillator.frequency.setValueAtTime(config.frequency, audioContext.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + config.duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + config.duration);
      
    } catch (error) {
      console.warn('Audio not available:', error);
    }
  }, [playSound]);

  // Vibrazione ottimizzata
  const triggerVibration = useCallback((vibrationType) => {
    if (!enableVibration || !navigator.vibrate) return;
    
    const patterns = {
      success: [100],
      error: [150, 50, 150],
      warning: [100, 50, 100],
      info: [50]
    };
    
    navigator.vibrate(patterns[vibrationType] || patterns.info);
  }, [enableVibration]);

  // Gestione apparizione notifica
  useEffect(() => {
    if (isVisible && !isEntering) {
      setIsEntering(true);
      setIsExiting(false);
      soundPlayedRef.current = false;
      
      // Feedback immediato
      setTimeout(() => {
        playNotificationSound(type);
        triggerVibration(type);
      }, 50);
    }
  }, [isVisible, isEntering, type, playNotificationSound, triggerVibration]);

  // Gestione progress bar e auto-close
  useEffect(() => {
    if (isVisible && autoClose && showProgressBar) {
      setProgress(100);
      
      const interval = 50;
      const step = (interval / duration) * 100;
      
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - step;
          if (newProgress <= 0) {
            clearInterval(progressIntervalRef.current);
            return 0;
          }
          return newProgress;
        });
      }, interval);
      
      timeoutRef.current = setTimeout(() => {
        handleClose();
      }, duration);
    }
    
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible, autoClose, showProgressBar, duration]);

  // Reset quando diventa invisibile
  useEffect(() => {
    if (!isVisible) {
      setIsEntering(false);
      setProgress(100);
      soundPlayedRef.current = false;
    }
  }, [isVisible]);

  // Gestione chiusura con animazione di uscita
  const handleClose = useCallback(() => {
    if (isExiting) return;
    
    setIsExiting(true);
    
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Attendi fine animazione prima di chiamare onClose
    setTimeout(() => {
      onClose();
    }, 300);
  }, [isExiting, onClose]);

  if (!isVisible) return null;

  return (
    <div 
      className={`notification-optimized ${type} ${size} ${isEntering ? 'entering' : ''} ${isExiting ? 'exiting' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="notification-close-btn"
        onClick={handleClose}
        aria-label="Chiudi notifica"
      >
        ×
      </button>
      
      <div className="notification-content-wrapper">
        <span className="notification-icon-simple">
          {type === 'success' && '✅'}
          {type === 'error' && '❌'}
          {type === 'warning' && '⚠️'}
          {type === 'info' && 'ℹ️'}
        </span>
        <span className="notification-text">
          {message}
        </span>
      </div>
      
      {showProgressBar && autoClose && (
        <div className="notification-progress-simple">
          <div 
            className="notification-progress-fill-simple"
            style={{ 
              width: `${progress}%`,
              transitionDuration: progress === 100 ? '0ms' : '50ms'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Notification;